import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Handshake } from "lucide-react";

export const grossCommission = (p) => {
  const m = p.mediation || {};
  if (m.commission_value == null) return null;
  if (m.commission_type === "valor") return m.commission_value;
  const price = p.price || m.asking_price;
  return price ? (price * m.commission_value) / 100 : null;
};

export const netCommission = (p) => {
  const g = grossCommission(p);
  const m = p.mediation || {};
  return g != null && m.share_agency ? g * (1 - (m.share_pct || 0) / 100) : g;
};

export const PartilhaComissao = ({ p, onDone }) => {
  const { user } = useAuth();
  const m = p.mediation || {};
  const [f, setF] = useState(null);
  const canEdit = user?.role !== "direccao";
  const g = grossCommission(p);
  const net = g != null && m.share_agency ? g * (1 - (m.share_pct || 0) / 100) : null;
  const save = async (clear) => {
    const pct = f.pct === "" ? null : Number(f.pct);
    if (!clear && (!f.agency.trim() || pct == null || pct < 0 || pct > 100)) { toast.error("Indique a agência e uma percentagem entre 0 e 100."); return; }
    try {
      await api.put(`/processes/${p.id}`, { mediation: clear ? { share_agency: null, share_pct: null } : { share_agency: f.agency.trim(), share_pct: pct } });
      toast.success(clear ? "Partilha removida" : "Partilha guardada"); setF(null); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  if (!m.share_agency && !canEdit) return null;
  return (
    <div className="bg-white rounded-[16px] px-6 py-4 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center gap-4 flex-wrap" data-testid="share-card">
      <Handshake size={18} className="text-ec-gold shrink-0" />
      <div className="flex-1 min-w-0 text-sm">
        {m.share_agency ? (
          <span data-testid="share-summary"><b className="text-ec-green">Partilha com {m.share_agency} — {m.share_pct ?? 0}%</b>{net != null && <span className="text-slate-500"> · comissão líquida da agência {fmtCurrency(net)}</span>}</span>
        ) : <span className="text-slate-500" data-testid="share-none">Sem partilha de comissão com outra agência.</span>}
      </div>
      {canEdit && <button onClick={() => setF({ agency: m.share_agency || "", pct: m.share_pct ?? "" })} data-testid="share-edit-btn" className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 text-ec-green hover:bg-slate-50 transition-colors">{m.share_agency ? "Alterar" : "Indicar partilha"}</button>}
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-ec-green">Partilha com outra agência</DialogTitle>
            <DialogDescription>A comissão prevista passa a ser a líquida, depois da parte da outra agência.</DialogDescription>
          </DialogHeader>
          {f && (
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>Agência</Label><Input className="mt-1.5" value={f.agency} onChange={(e) => setF({ ...f, agency: e.target.value })} data-testid="share-agency" /></div>
              <div><Label>% da comissão</Label><Input className="mt-1.5" type="number" min="0" max="100" value={f.pct} onChange={(e) => setF({ ...f, pct: e.target.value })} data-testid="share-pct" /></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {m.share_agency && <button onClick={() => save(true)} data-testid="share-remove" className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">Remover partilha</button>}
            <button onClick={() => save(false)} data-testid="share-save" className="bg-ec-green text-white px-4 py-2 rounded-lg hover:bg-ec-greenDark transition-colors">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
